import { useMemo, useState } from 'react';
import { Box, Link, Theme } from '@mui/material';
import { Dialog } from '@components/dialog';
import { Table } from '@components/table/Table';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { format } from 'date-fns';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { useGetNotification, useSearchNotifications } from '@services/notification';
import type {
  QuoteGetResponse,
  PolicyGetResponse,
  NotificationsSearchRequestQuery,
  NotificationsSearchResponse,
} from '@lio/api-types';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { adjustFontSize } from '@theme/styleOverrides/typography';

export type ExternalSentEmailsModalProps = {
  open: boolean;
  onClose: () => void;
  context: 'quote' | 'policy';
  quote?: QuoteGetResponse;
  policy?: PolicyGetResponse;
};

export type NotificationRow = NotificationsSearchResponse['items'][number];

type TExternalEmailsTable = {
  items: NotificationRow[];
  params: NotificationsSearchRequestQuery;
};

export const ExternalSentEmailsModal = ({ open, onClose, context, quote, policy }: ExternalSentEmailsModalProps) => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const [selectedNotificationId, setSelectedNotificationId] = useState<string | undefined>(undefined);
  const [showJsonDialog, setShowJsonDialog] = useState(false);

  const title = useMemo(() => {
    if (context === 'policy' && policy) {
      const policyNumber = getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber);
      return `Emails sent to External Users for ${policyNumber}`;
    }

    // Quote context: try to format via policy number if available, else fallback to #quote number
    const formatted = quote?.policyNumber?.policyNumber
      ? `#${quote?.policyNumber?.policyNumber}`
      : `#${quote?.id ?? ''}`;
    return `Emails sent to External Users for ${formatted}`;
  }, [context, policy, quote]);

  const columns: TableGridBaseColDef<NotificationRow>[] = useMemo(() => {
    return [
      {
        field: 'heading',
        headerName: 'Notification',
        flex: 0.5,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) => {
          return row?.heading ?? '-';
        },
      },
      {
        field: 'recipient',
        headerName: 'Recipient',
        minWidth: 220,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => `${row.user.firstName ?? ''} ${row.user.lastName ?? ''} (${row.user.email ?? '-'})`,
      },
      {
        field: 'sentOn',
        headerName: 'Sent On',
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => {
          const dt = row?.emailNotification?.sentAt;
          if (!dt) return null;
          return format(new Date(dt as any), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds));
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Link
            component='button'
            underline='hover'
            sx={{ color: 'secondary.main', fontWeight: 500 }}
            onClick={() => {
              setSelectedNotificationId(row.id);
              setShowJsonDialog(true);
            }}
          >
            View email data
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row?.emailNotification?.emailNotificationServiceResponse?.status ?? '-',
      },
    ];
  }, [clockFormat]);

  const useSearchNotificationsWrapper = (params: Partial<NotificationsSearchRequestQuery>) => {
    let baseParams = {
      ...params,
      userRoleType: 'External',
    } as NotificationsSearchRequestQuery;

    let enabled = false;

    if (context === 'quote' && quote?.id) {
      enabled = true;
      baseParams = { ...baseParams, quoteId: quote.id };
    } else if (context === 'policy' && policy?.id) {
      enabled = true;
      baseParams = { ...baseParams, policyId: policy.id };
    }

    return useSearchNotifications(baseParams, enabled);
  };

  const { notification } = useGetNotification(selectedNotificationId);

  const noDataMessage = useMemo(() => {
    const label = context === 'quote' ? 'quote' : 'policy';
    return `No External Emails have been sent on this ${label}. Either no email notifications should have sent, or the User's preferences have turned emails off. Reach out to Support if you have any questions or would like clarification.`;
  }, [context]);

  return (
    <Dialog
      open={open}
      title={title}
      secondaryBtnText='Exit'
      secondaryBtnAction={onClose}
      handleCloseDialog={onClose}
      primaryBtnText={undefined}
      size='extraLarge'
      fullWidth
    >
      <Box sx={{ padding: '16px 24px', minHeight: '400px' }}>
        <Table<TExternalEmailsTable>
          ariaLabel='external-emails-table'
          onLoadData={useSearchNotificationsWrapper}
          columns={columns}
          paginationConfig={{ currentPage: 0, rowsPerPage: 10, pageOffset: 0 }}
          noResultsConfig={{ disableNoMatchFound: false, noResultsMessage: noDataMessage }}
        />
      </Box>

      {showJsonDialog && (
        <Dialog
          open={showJsonDialog}
          title={`Email Data`}
          secondaryBtnText='Exit'
          secondaryBtnAction={() => setShowJsonDialog(false)}
          handleCloseDialog={() => setShowJsonDialog(false)}
          primaryBtnText={undefined}
          size='large'
          fullWidth
        >
          <Box sx={{ padding: '8px 12px', minHeight: '300px' }}>
            <LazyJsonViewer
              value={notification?.emailNotification?.emailData}
              defaultInspectDepth={100}
              objectSortKeys
              sx={{
                overflowY: 'auto',
                fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
                fontWeight: 300,
                backgroundColor: 'white',
              }}
            />
          </Box>
        </Dialog>
      )}
    </Dialog>
  );
};
